import React from 'react';
import { Save } from 'lucide-react';
import Card, { CardHeader } from '../../common/Card';
import FormField, { Input, Textarea } from '../../common/FormField';
import Button from '../../common/Button';
import CannedJobsSection from '../parts/CannedJobsSection';
import PaymentsSection from '../parts/PaymentsSection';

// Extracted verbatim from JobManagement's settings tab block (Task 7),
// restyled to B2 primitives (Task 8 of the mechanic UI restyle).
// Presentational only — it calls no API directly; `saveUserPreferences`
// comes from useMechanicPreferences via the orchestrator.
//
// The pre-conversion markup wrapped everything in one card under an
// "Invoice & Job Settings" header with three h4 subsections (Default
// Rates / Business Information / Default Invoice Notes). That single-card
// shape is replaced here by two Cards with their own CardHeaders — a
// deliberate, spec-directed restructuring, not a regression. Every field,
// hint, and placeholder the original subsections carried is preserved
// below; only the decorative per-section icons (DollarSign/User/FileText)
// and the outer wrapper are dropped, since no other converted tab
// (Dashboard/Customers/Diagnostics/Documents) puts icons in a CardHeader.
//
// The tax-rate field displays a percentage while the stored value is a
// fraction: `(default_tax_rate * 100).toFixed(1)` on display, `/ 100` on
// change. Preserved exactly — getting this wrong would shift every
// invoice's tax by two orders of magnitude.
const SettingsTab = ({
  userPreferences,
  setUserPreferences,
  savingPreferences,
  saveUserPreferences
}) => (
  <div className="space-y-6">
    <Card padding={false}>
      <CardHeader
        title="Default Rates"
        subtitle="Used when generating new invoices"
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
        <FormField
          label="Default Labor Rate ($/hour)"
          hint="This rate will be used as the default when generating invoices"
        >
          <Input
            type="number"
            min="0"
            step="5"
            value={userPreferences.default_labor_rate}
            onChange={(e) => setUserPreferences((prev) => ({
              ...prev,
              default_labor_rate: parseFloat(e.target.value) || 0,
            }))}
          />
        </FormField>
        <FormField
          label="Default Tax Rate (%)"
          hint="Tax rate to apply to invoices (e.g., 8 for 8%)"
        >
          <Input
            type="number"
            min="0"
            max="100"
            step="0.1"
            value={(userPreferences.default_tax_rate * 100).toFixed(1)}
            onChange={(e) => setUserPreferences((prev) => ({
              ...prev,
              default_tax_rate: (parseFloat(e.target.value) || 0) / 100,
            }))}
          />
        </FormField>
      </div>
    </Card>

    <Card padding={false}>
      <CardHeader
        title="Business Information"
        subtitle="Optional — displayed on your invoices"
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
        <FormField label="Business Name">
          <Input
            type="text"
            placeholder="Your Shop Name"
            value={userPreferences.business_name || ''}
            onChange={(e) => setUserPreferences((prev) => ({ ...prev, business_name: e.target.value }))}
          />
        </FormField>
        <FormField label="Business Phone">
          <Input
            type="tel"
            placeholder="(555) 123-4567"
            value={userPreferences.business_phone || ''}
            onChange={(e) => setUserPreferences((prev) => ({ ...prev, business_phone: e.target.value }))}
          />
        </FormField>
        <FormField label="Business Address" className="md:col-span-2">
          <Textarea
            rows={2}
            placeholder="123 Main St, City, State ZIP"
            value={userPreferences.business_address || ''}
            onChange={(e) => setUserPreferences((prev) => ({ ...prev, business_address: e.target.value }))}
          />
        </FormField>
        <FormField
          htmlFor="registration-number"
          label="State registration number"
          hint="e.g. California ARD #. Printed on every estimate and invoice."
        >
          <Input
            id="registration-number"
            type="text"
            value={userPreferences.registration_number || ''}
            onChange={(e) => setUserPreferences((prev) => ({ ...prev, registration_number: e.target.value }))}
          />
        </FormField>
        <FormField
          htmlFor="warranty-terms"
          label="Warranty terms"
          hint="Printed at the foot of every estimate and invoice."
          className="md:col-span-2"
        >
          <Textarea
            id="warranty-terms"
            rows={2}
            placeholder="Parts and labour guaranteed for 12 months or 12,000 miles."
            value={userPreferences.warranty_terms || ''}
            onChange={(e) => setUserPreferences((prev) => ({ ...prev, warranty_terms: e.target.value }))}
          />
        </FormField>
        <FormField
          label="Default Invoice Notes"
          hint="Prefilled into the notes field of every new invoice"
          className="md:col-span-2"
        >
          <Textarea
            rows={3}
            placeholder="Payment is due within 30 days. Thank you for your business!"
            value={userPreferences.invoice_notes || ''}
            onChange={(e) => setUserPreferences((prev) => ({ ...prev, invoice_notes: e.target.value }))}
          />
        </FormField>
      </div>
    </Card>

    <PaymentsSection />
    <CannedJobsSection />

    <div className="flex justify-end">
      <Button
        variant="primary"
        icon={Save}
        loading={savingPreferences}
        disabled={savingPreferences}
        onClick={saveUserPreferences}
        className="min-h-touch"
      >
        {savingPreferences ? 'Saving…' : 'Save Settings'}
      </Button>
    </div>
  </div>
);

export default SettingsTab;
